/**
 * Links Section
 * Displays external links and contact information
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DcsApp } from '../../types/general-settings-types';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';

interface LinksSectionProps {
  app: DcsApp;
  onFieldChange: (field: string, value: any) => void;
  hasChanges?: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

export const LinksSection = ({ app, onFieldChange, hasChanges, isOpen, onToggle }: LinksSectionProps) => {
  const { t } = useTranslation('assignEv');

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('general.linksSection.title')}</h3>
          {hasChanges && (
            <span className="ml-2 px-3 py-1 text-xs font-bold text-white bg-orange-500 rounded-full shadow-md">
              {t('badges.modified')}
            </span>
          )}
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-6 py-4 bg-white space-y-8">
          {/* ===== CONTACT INFORMATION ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.linksSection.subsections.contactInformation')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.linksSection.fields.contactNumber')}
                </Label>
                <Input
                  value={app.contactUsNumber || ''}
                  onChange={(e) =>
                    onFieldChange('contactUsNumber', e.target.value)
                  }
                  placeholder={t('general.linksSection.fields.contactNumberPlaceholder')}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.linksSection.fields.contactUrl')}
                </Label>
                <Input
                  value={app.contactUsUrl || ''}
                  onChange={(e) =>
                    onFieldChange('contactUsUrl', e.target.value)
                  }
                  placeholder={t('general.linksSection.fields.urlPlaceholder')}
                  className="bg-white border-gray-300"
                />
              </div>
            </div>
          </div>

          {/* ===== LEGAL DOCUMENTS ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.linksSection.subsections.legalDocuments')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.linksSection.fields.termsConditions')}
                </Label>
                <Input
                  value={app.termsUrl || ''}
                  onChange={(e) =>
                    onFieldChange('termsUrl', e.target.value)
                  }
                  placeholder={t('general.linksSection.fields.urlPlaceholder')}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.linksSection.fields.privacyPolicy')}
                </Label>
                <Input
                  value={app.privacyUrl || ''}
                  onChange={(e) =>
                    onFieldChange('privacyUrl', e.target.value)
                  }
                  placeholder={t('general.linksSection.fields.urlPlaceholder')}
                  className="bg-white border-gray-300"
                />
              </div>
            </div>
          </div>

          {/* ===== ADDITIONAL LINKS ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.linksSection.subsections.additionalLinks')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.linksSection.fields.website')}
                </Label>
                <Input
                  value={app.website || ''}
                  onChange={(e) =>
                    onFieldChange('website', e.target.value)
                  }
                  placeholder={t('general.linksSection.fields.urlPlaceholder')}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.linksSection.fields.about')}
                </Label>
                <Input
                  value={app.aboutUrl || ''}
                  onChange={(e) =>
                    onFieldChange('aboutUrl', e.target.value)
                  }
                  placeholder={t('general.linksSection.fields.urlPlaceholder')}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.linksSection.fields.accessibility')}
                </Label>
                <Input
                  value={app.accessibilityUrl || ''}
                  onChange={(e) =>
                    onFieldChange('accessibilityUrl', e.target.value)
                  }
                  placeholder={t('general.linksSection.fields.urlPlaceholder')}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.linksSection.fields.qrRedirectUrlTemplate')}
                </Label>
                <Input
                  value={app.qrRedirectUrlTemplate || ''}
                  onChange={(e) =>
                    onFieldChange('qrRedirectUrlTemplate', e.target.value)
                  }
                  placeholder={t('general.linksSection.fields.urlPlaceholder')}
                  className="bg-white border-gray-300"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
